import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropzone", "previewSection", "previewContainer"]

  connect() {
    this.items = []
    this.nextId = 0
    this.draggedTile = null

    this.boundReorderStart = this.reorderStart.bind(this)
    this.boundReorderOver = this.reorderOver.bind(this)
    this.boundReorderDrop = this.reorderDrop.bind(this)
    this.boundReorderEnd = this.reorderEnd.bind(this)

    const container = this.previewContainerTarget
    container.addEventListener("dragstart", this.boundReorderStart)
    container.addEventListener("dragover", this.boundReorderOver)
    container.addEventListener("drop", this.boundReorderDrop)
    container.addEventListener("dragend", this.boundReorderEnd)
  }

  disconnect() {
    const container = this.previewContainerTarget
    container.removeEventListener("dragstart", this.boundReorderStart)
    container.removeEventListener("dragover", this.boundReorderOver)
    container.removeEventListener("drop", this.boundReorderDrop)
    container.removeEventListener("dragend", this.boundReorderEnd)

    this.items.forEach(item => URL.revokeObjectURL(item.url))
  }

  // Dropzone: drag image files in from the OS
  dragover(event) {
    if (this.draggedTile) return

    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add("border-emerald-500", "bg-emerald-50")
    this.dropzoneTarget.classList.remove("border-slate-300")
  }

  dragleave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-emerald-500", "bg-emerald-50")
    this.dropzoneTarget.classList.add("border-slate-300")
  }

  drop(event) {
    if (this.draggedTile) return

    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-emerald-500", "bg-emerald-50")
    this.dropzoneTarget.classList.add("border-slate-300")

    this.addFiles(event.dataTransfer.files)
  }

  preview(event) {
    this.addFiles(event.target.files)
  }

  browse() {
    this.inputTarget.click()
  }

  addFiles(fileList) {
    const images = Array.from(fileList || []).filter(file => file.type.startsWith("image/"))
    if (images.length === 0) return

    images.forEach(file => {
      this.items.push({ id: this.nextId++, file, url: URL.createObjectURL(file) })
    })

    this.syncInput()
    this.renderPreviews()
  }

  removeImage(event) {
    const id = Number(event.currentTarget.dataset.itemId)
    const index = this.items.findIndex(item => item.id === id)
    if (index === -1) return

    URL.revokeObjectURL(this.items[index].url)
    this.items.splice(index, 1)

    this.syncInput()
    this.renderPreviews()
  }

  // Keep the file input's FileList in the same order as the previews, so the
  // server assigns image positions to match what the host arranged.
  syncInput() {
    const dt = new DataTransfer()
    this.items.forEach(item => dt.items.add(item.file))
    this.inputTarget.files = dt.files
  }

  renderPreviews() {
    this.previewContainerTarget.innerHTML = ""

    if (this.items.length === 0) {
      this.previewSectionTarget.classList.add("hidden")
      this.inputTarget.value = ""
      return
    }

    this.previewSectionTarget.classList.remove("hidden")

    this.items.forEach(item => {
      const tile = document.createElement("div")
      tile.className = "relative group cursor-grab active:cursor-grabbing"
      tile.draggable = true
      tile.dataset.itemId = item.id
      tile.innerHTML = `
        <img src="${item.url}" class="w-full h-24 object-cover rounded pointer-events-none" alt="Preview of selected image" />
        <button type="button"
                data-action="click->listing-edit-images#removeImage"
                data-item-id="${item.id}"
                class="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors cursor-pointer">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      `
      this.previewContainerTarget.appendChild(tile)
    })
  }

  reorderStart(event) {
    const tile = this.tileFor(event.target)
    if (!tile) return

    this.draggedTile = tile
    tile.classList.add("opacity-50")
    event.dataTransfer.effectAllowed = "move"
  }

  reorderOver(event) {
    if (!this.draggedTile) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const target = this.tileFor(event.target)
    if (!target || target === this.draggedTile) return

    const tiles = [...this.previewContainerTarget.children]
    if (tiles.indexOf(this.draggedTile) < tiles.indexOf(target)) {
      target.after(this.draggedTile)
    } else {
      target.before(this.draggedTile)
    }
  }

  reorderDrop(event) {
    if (this.draggedTile) event.preventDefault()
  }

  reorderEnd() {
    if (!this.draggedTile) return

    this.draggedTile.classList.remove("opacity-50")
    this.draggedTile = null

    const order = [...this.previewContainerTarget.children].map(tile => Number(tile.dataset.itemId))
    this.items.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    this.syncInput()
  }

  tileFor(element) {
    return element.closest ? element.closest("[draggable='true']") : null
  }
}
